/* Ritual sounds — Energy releases (Ignite · Amplify · Manifest) and the
   universal "fused" seal used by every tile. Built on the luxe kit. */
import { getAudioCtx } from './audioCore';
import { makeBus, bell, swish, gliss, pluck } from './luxeSfx';
import { vibrate } from './haptics';

let master = null;
const out = () => {
  const ctx = getAudioCtx();
  if (!ctx) return null;
  if (!master || master.context !== ctx) {
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
  }
  return { ctx, bus: makeBus(ctx, master) };
};

/* Deep fire whoosh rising into a bright bell — burning the old away. */
export const igniteSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  swish(ctx, bus, t, 0.9, 0.35, 180, 2600);
  swish(ctx, bus, t + 0.15, 0.7, 0.2, 400, 5200);
  bell(ctx, bus, 130.81, t + 0.05, 0.16, 2.4);
  bell(ctx, bus, 783.99, t + 0.85, 0.12, 2.2);
  bell(ctx, bus, 1174.66, t + 0.95, 0.08, 2.0);
  vibrate([30, 40, 80]);
};

/* Warm harp bloom with a shimmering crown — gratitude amplified. */
export const amplifySfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  gliss(ctx, bus, t, [392, 493.88, 587.33, 783.99, 987.77, 1174.66], 0.08, 0.13);
  bell(ctx, bus, 1567.98, t + 0.55, 0.08, 2.2);
  vibrate([25, 30, 50]);
};

/* Low royal decree bell, then a rising command chord — manifestation sealed. */
export const manifestSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  bell(ctx, bus, 98, t, 0.22, 3.0);
  [261.63, 392, 523.25, 659.25].forEach((f, i) => pluck(ctx, bus, f, t + 0.35 + i * 0.09, 0.12, 1.8));
  bell(ctx, bus, 1046.5, t + 0.8, 0.1, 2.4);
  vibrate([40, 30, 40, 30, 90]);
};

/* Universal "Fused" seal — crystal chord + firm double pulse. */
export const fuseSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  [523.25, 659.25, 783.99].forEach((f, i) => bell(ctx, bus, f, t + i * 0.06, 0.12, 2.0));
  bell(ctx, bus, 1567.98, t + 0.3, 0.07, 1.6);
  vibrate([60, 40, 120]);
};

/* Oath seal — low wax-press thud into a sovereign major chord. */
export const oathSealSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  bell(ctx, bus, 73.42, t, 0.24, 2.6);
  swish(ctx, bus, t, 0.35, 0.18, 300, 1800);
  [293.66, 369.99, 440, 587.33].forEach((f, i) => bell(ctx, bus, f, t + 0.25 + i * 0.07, 0.1, 2.2));
  bell(ctx, bus, 1174.66, t + 0.6, 0.07, 2.0);
  vibrate([80, 50, 30, 50, 140]);
};

/* Gratitude Rush — quick bright sparkle run; pitch climbs with each hit. */
export const gratitudeRushSfx = (n = 1) => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  const lift = Math.pow(2, Math.min(n - 1, 7) / 12);
  gliss(ctx, bus, t, [659.25, 783.99, 987.77, 1318.51].map((f) => f * lift), 0.045, 0.11);
  bell(ctx, bus, 1975.53 * lift, t + 0.2, 0.06, 1.4);
  vibrate([18, 25, 35]);
};

/* Laser Lock — tight rising sweep that snaps shut on a pure tone. */
export const laserLockSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  swish(ctx, bus, t, 0.5, 0.2, 600, 7000);
  pluck(ctx, bus, 880, t + 0.45, 0.14, 1.2);
  bell(ctx, bus, 1760, t + 0.5, 0.09, 2.2);
  vibrate([20, 20, 20, 20, 90]);
};

/* Distraction shattered — glassy crack + tiny pulse. */
export const shatterSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  swish(ctx, bus, t, 0.12, 0.16, 2400, 9000);
  pluck(ctx, bus, 1318.51 + Math.random() * 400, t + 0.02, 0.08, 0.5);
  vibrate(12);
};

/* Daily Crown forged — anvil strike, royal fanfare chord, shimmering crown. */
export const crownSfx = () => {
  const o = out(); if (!o) return;
  const { ctx, bus } = o; const t = ctx.currentTime;
  bell(ctx, bus, 65.41, t, 0.26, 3.2);
  bell(ctx, bus, 130.81, t + 0.02, 0.14, 2.6);
  [261.63, 329.63, 392, 523.25, 659.25, 783.99].forEach((f, i) => pluck(ctx, bus, f, t + 0.4 + i * 0.08, 0.12, 2.2));
  gliss(ctx, bus, t + 1.0, [1046.5, 1318.51, 1567.98, 2093], 0.06, 0.08);
  vibrate([100, 60, 40, 60, 40, 60, 200]);
};

/* Laser Lock focus bed — warm A-major pad with a gentle 10 Hz alpha beat
   (left/right detune), slow breathing swell and a soft crystal chime every ~6s.
   Returns stop() which fades out smoothly. */
export const startFocusAmbience = (seconds = 45) => {
  const o = out(); if (!o) return () => {};
  const { ctx } = o;
  const t = ctx.currentTime;
  const bus = ctx.createGain();
  bus.gain.setValueAtTime(0.0001, t);
  bus.gain.exponentialRampToValueAtTime(0.22, t + 2.5);
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 1400; lp.Q.value = 0.4;
  lp.connect(bus); bus.connect(ctx.destination);
  const breath = ctx.createOscillator(); const breathAmt = ctx.createGain();
  breath.frequency.value = 0.1; breathAmt.gain.value = 0.06;
  breath.connect(breathAmt); breathAmt.connect(bus.gain); breath.start(t);
  const nodes = [breath];
  const voice = (f, pan, g) => {
    const osc = ctx.createOscillator(); const gn = ctx.createGain(); const p = ctx.createStereoPanner();
    osc.type = 'sine'; osc.frequency.value = f; gn.gain.value = g; p.pan.value = pan;
    osc.connect(gn); gn.connect(p); p.connect(lp); osc.start(t); nodes.push(osc);
  };
  voice(110, -0.3, 0.30); voice(164.81, 0.3, 0.18); voice(220, -1, 0.16); voice(230, 1, 0.16);
  voice(277.18, 0.2, 0.08); voice(329.63, -0.2, 0.06);
  const chimes = [880, 1108.73, 1318.51, 987.77, 1174.66];
  let k = 0;
  const timer = setInterval(() => {
    const now = ctx.currentTime; const f = chimes[k++ % chimes.length];
    const osc = ctx.createOscillator(); const gn = ctx.createGain();
    osc.type = 'sine'; osc.frequency.value = f;
    gn.gain.setValueAtTime(0.0001, now); gn.gain.exponentialRampToValueAtTime(0.05, now + 0.05);
    gn.gain.exponentialRampToValueAtTime(0.0001, now + 3.5);
    osc.connect(gn); gn.connect(lp); osc.start(now); osc.stop(now + 3.6);
  }, 6000);
  let stopped = false;
  const stop = () => {
    if (stopped) return; stopped = true;
    clearInterval(timer);
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(Math.max(bus.gain.value, 0.0001), now);
    bus.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
    nodes.forEach((n) => { try { n.stop(now + 1.7); } catch { /* ignore */ } });
    setTimeout(() => { try { bus.disconnect(); } catch { /* ignore */ } }, 1900);
  };
  setTimeout(stop, (seconds + 1) * 1000);
  return stop;
};
